"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import styles from "./FinalPlayerStandings.module.css";

export function PostPlayerPodium({
  cards,
  label,
  navigationLabel,
}: {
  cards: { id: string; rank: number; label: string; content: ReactNode }[];
  label: string;
  navigationLabel: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const id = useId();
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Observe a card, not the padded podium wrapper, to time the entrance.
    const element = track.current?.firstElementChild;
    if (!element || !window.IntersectionObserver) return;
    const motion = window.matchMedia(
      "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
    );
    let observer: IntersectionObserver | undefined;
    const configure = () => {
      observer?.disconnect();
      setVisible(false);
      if (motion.matches) {
        observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 1 / 3) {
              setVisible(true);
              observer?.disconnect();
            }
          },
          { threshold: 1 / 3 },
        );
        observer.observe(element);
      }
    };
    configure();
    motion.addEventListener("change", configure);
    return () => {
      observer?.disconnect();
      motion.removeEventListener("change", configure);
    };
  }, []);

  const selectCard = (index: number) => {
    const element = track.current;
    const card = element?.children[index] as HTMLElement | undefined;
    if (!element || !card) return;
    const left =
      card.getBoundingClientRect().left -
      element.getBoundingClientRect().left +
      element.scrollLeft -
      (element.clientWidth - card.offsetWidth) / 2;
    setActive(index);
    element.scrollTo({
      left,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  };
  const syncActive = () => {
    const element = track.current;
    if (!element) return;
    const center =
      element.getBoundingClientRect().left + element.clientWidth / 2;
    let closest = 0;
    let distance = Infinity;
    Array.from(element.children).forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const delta = Math.abs(rect.left + rect.width / 2 - center);
      if (delta < distance) {
        closest = index;
        distance = delta;
      }
    });
    setActive(closest);
  };

  return (
    <div ref={root} className={styles.podium} data-visible={visible}>
      <div
        id={id}
        ref={track}
        className={styles.track}
        role="group"
        aria-label={label}
        tabIndex={0}
        onScroll={syncActive}
        onKeyDown={(event) => {
          if (
            event.target !== event.currentTarget ||
            !window.matchMedia("(max-width: 767px)").matches
          )
            return;
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            selectCard(
              Math.max(
                0,
                Math.min(
                  cards.length - 1,
                  active + (event.key === "ArrowRight" ? 1 : -1),
                ),
              ),
            );
          }
        }}
      >
        {cards.map((card) => (
          <div key={card.id} className={styles.slide} data-rank={card.rank}>
            {card.content}
          </div>
        ))}
      </div>
      <nav className={styles.dots} aria-label={navigationLabel}>
        {cards.map((card, index) => (
          <button
            key={card.id}
            type="button"
            className={styles.dot}
            aria-label={card.label}
            aria-current={index === active ? "true" : undefined}
            aria-controls={id}
            onClick={() => selectCard(index)}
          >
            <span aria-hidden="true" />
          </button>
        ))}
      </nav>
    </div>
  );
}
